import {
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ServiceProviderHandler,
  stafferToNamedDoc,
  SterilisationRecord,
} from '@principle-theorem/principle-core';
import {
  IStaffer,
  ISterilisationCycle,
  SterilisableItem,
  ISterilisationMachine,
  SterilisationStatus,
  type IBrand,
  type IPatient,
  type IPractice,
  type ISterilisationRecord,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  Timestamp,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toNamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientSterilisationDestinationEntity,
  ISterilisationRecordBaseJobData,
  ISterilisationRecordMigrationData,
} from '../../../destination/entities/sterilisation-records';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import {
  D4WPatientTreatmentNote,
  PatientTreatmentNoteSourceEntity,
} from '../../source/entities/patient-treatment-note';
import { SterilisationRecordSourceEntity } from '../../source/entities/sterilisation-records';
import { D4WItemCodeToNoteMappingHandler } from '../mappings/item-code-to-note';
import { D4WPracticeMappingHandler } from '../mappings/practices';
import { PatientDestinationEntity } from './patients';
import { D4WSterilisationMachineDestinationEntity } from './sterilisation-machine';
import { D4WSterilisationCycleDestinationEntity } from './sterilisation-cycle';
import { D4WSterilisationEquipmentDestinationEntity } from './sterilisations-equipment';
import { D4WStafferMappingHandler, resolveD4WStaffer } from '../mappings/staff';
import { D4WSterilisationMachineMappingHandler } from '../mappings/sterilisation-machine';
import { D4WSterilisationEquipmentMappingHandler } from '../mappings/sterilisation-equipment';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { STERILISATION_CYCLE_RESOURCE_TYPE } from '../../../destination/entities/sterilisation-cycle';
import { resolveMappedItem } from '../../../base-custom-mapping-handler';
import { STERILISATION_EQUIPMENT_RESOURCE_TYPE } from '../../../mappings/sterilisation-equipment';
import { StafferDestinationEntity } from './staff';
import { initVersionedSchema } from '@principle-theorem/editor';

interface IPatientSterilisationJobData
  extends ISterilisationRecordBaseJobData<D4WPatient> {
  brand: WithRef<IBrand>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  sourceItemCodes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
  sterilisationMachines: WithPrimaryKey<
    ITranslationMap<ISterilisationMachine>
  >[];
  sterilisationEquipment: WithPrimaryKey<ITranslationMap<SterilisableItem>>[];
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
}

export class SterilisationRecordDestinationEntity extends BasePatientSterilisationDestinationEntity<
  D4WPatient,
  IPatientSterilisationJobData
> {
  override filters = [
    new PracticeIdFilter<IPatientSterilisationJobData>((jobData) =>
      jobData.sourcePatient.data.practice_id.toString()
    ),
    new PatientIdFilter<IPatientSterilisationJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatmentNotes: new PatientTreatmentNoteSourceEntity(),
    sterilisationRecords: new SterilisationRecordSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
    sterilisationMachines: new D4WSterilisationMachineDestinationEntity(),
    sterilisationCycles: new D4WSterilisationCycleDestinationEntity(),
    sterilisationEquipment: new D4WSterilisationEquipmentDestinationEntity(),
  };

  customMappings = {
    staff: new D4WStafferMappingHandler(),
    itemCodes: new D4WItemCodeToNoteMappingHandler(),
    practices: new D4WPracticeMappingHandler(),
    sterilisationMachines: new D4WSterilisationMachineMappingHandler(),
    sterilisationEquipment: new D4WSterilisationEquipmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientSterilisationJobData[]> {
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const brand$ = PracticeMigration.brand$(migration);
    const sterilisationMachines$ =
      this.customMappings.sterilisationMachines.getRecords(translationMap);
    const sterilisationEquipment$ =
      this.customMappings.sterilisationEquipment.getRecords(translationMap);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([
      brand$,
      practices$,
      sourceItemCodes$,
      sterilisationMachines$,
      sterilisationEquipment$,
      staff$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            brand,
            practices,
            sourceItemCodes,
            sterilisationMachines,
            sterilisationEquipment,
            staff,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            brand,
            practices,
            sourceItemCodes,
            sterilisationMachines,
            sterilisationEquipment,
            staff,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientSterilisationJobData
  ): Promise<
    | ISterilisationRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient ${sourcePatientId}`
        );
      }

      const treatmentNotes =
        await this.sourceEntities.treatmentNotes.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const patient = await Firestore.getDoc(patientRef);

      const sterilisationRecords = await this._buildSterilisationRecords(
        migration,
        sourcePatientId,
        data,
        patient,
        translationMap
      );

      const sterilisationRecordsFromNotes =
        await this._buildSterilisationNotesFromTreatments(
          treatmentNotes,
          patient,
          data,
          translationMap
        );

      return {
        sterilisationRecords: [
          ...sterilisationRecordsFromNotes,
          ...sterilisationRecords,
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  private async _buildSterilisationRecords(
    migration: WithRef<IPracticeMigration>,
    sourcePatientId: string,
    data: IPatientSterilisationJobData,
    patient: WithRef<IPatient>,
    translationMap: TranslationMapHandler
  ): Promise<
    (ISterilisationRecord &
      IHasSourceIdentifier & {
        createdAt: Timestamp;
      })[]
  > {
    return resolveSequentially(
      await this.sourceEntities.sterilisationRecords.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      ),
      async (record) => {
        const practiceId = record.data.practice_id.toString();
        const practiceMap = data.practices.find(
          (searchPractice) =>
            searchPractice.sourceIdentifier === practiceId.toString()
        );

        if (!practiceMap?.destinationIdentifier) {
          throw new Error(`Can't find practice with id ${practiceId}`);
        }
        const practice = await Firestore.getDoc(
          practiceMap?.destinationIdentifier
        );

        const staffer = await resolveD4WStaffer(
          record.data.staff_id.toString(),
          data.staff,
          translationMap,
          false
        );

        const cycleMapping = await translationMap.getBySource(
          record.data.cycle_id.toString(),
          STERILISATION_CYCLE_RESOURCE_TYPE
        );

        if (!cycleMapping?.destinationIdentifier) {
          throw new Error(`Can't find cycle with id ${record.data.cycle_id}`);
        }

        const cycleRef = await Firestore.getDoc(
          cycleMapping.destinationIdentifier as DocumentReference<ISterilisationCycle>
        );

        let equipment: WithRef<SterilisableItem> | undefined;
        if (record.data.pack_content_id) {
          try {
            equipment = await resolveMappedItem<SterilisableItem>(
              record.data.pack_content_id.toString(),
              data.sterilisationEquipment,
              translationMap,
              STERILISATION_EQUIPMENT_RESOURCE_TYPE
            );
          } catch {
            equipment = undefined;
          }
        }

        return {
          ...SterilisationRecord.init({
            data: record.data.barcode,
            practice: toNamedDocument(practice),
            patient: toNamedDocument(patient),
            scannedBy: stafferToNamedDoc(staffer),
            cycle: toNamedDocument(cycleRef),
            pack: equipment ? toNamedDocument(equipment) : undefined,
            notes: record.data.contents.length
              ? initVersionedSchema(record.data.contents)
              : undefined,
          }),
          sourceIdentifier: `sterilisationRecord-${record.data.id.toString()}`,
          createdAt: record.record.translations.dateUsed,
          status: SterilisationStatus.Passed,
          deleted: false,
        };
      }
    );
  }

  private async _buildSterilisationNotesFromTreatments(
    notes: IGetRecordResponse<D4WPatientTreatmentNote>[],
    patient: WithRef<IPatient>,
    data: IPatientSterilisationJobData,
    translationMap: TranslationMapHandler
  ): Promise<
    (ISterilisationRecord &
      IHasSourceIdentifier & {
        createdAt: Timestamp;
      })[]
  > {
    return compact(
      await resolveSequentially(notes, async (note) => {
        const procedureCode = note.data.item_code;
        const code = ServiceProviderHandler.findServiceCode(procedureCode);
        if (code) {
          return;
        }

        const mappedCode = data.sourceItemCodes.find(
          (sourceItemCode) =>
            sourceItemCode.sourceIdentifier === procedureCode &&
            !!sourceItemCode.destinationValue &&
            sourceItemCode.destinationValue.value ===
              ItemCodeNoteType.SterilisationRecord
        );

        if (!mappedCode) {
          // eslint-disable-next-line no-console
          console.error(
            `Couldn't find mapped code for ${procedureCode} with id ${note.data.item_id}`
          );
          return;
        }

        const practiceId = note.data.practice_id.toString();
        const practiceMap = data.practices.find(
          (searchPractice) =>
            searchPractice.sourceIdentifier === practiceId.toString()
        );

        if (!practiceMap?.destinationIdentifier) {
          throw new Error(`Can't find practice with id ${practiceId}`);
        }
        const practice = await Firestore.getDoc(
          practiceMap?.destinationIdentifier
        );
        const staffer = await resolveD4WStaffer(
          note.data.provider_id.toString(),
          data.staff,
          translationMap
        );

        return {
          data: note.data.content,
          practice: toNamedDocument(practice),
          patient: toNamedDocument(patient),
          sourceIdentifier: `treatmentNote-${note.data.id.toString()}`,
          createdAt: note.record.translations.createdAt,
          status: SterilisationStatus.Passed,
          scannedBy: staffer,
          deleted: false,
        };
      })
    );
  }
}
